// src/Pages/ProfilePage.js
import React, { useState, useRef, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { doc, updateDoc, collection, getDocs, addDoc, deleteDoc } from 'firebase/firestore';
import { db, storage } from '../firebaseConfig';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './ProfilePage.css';
import { DndContext, useSensor, useSensors, PointerSensor, TouchSensor, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';




export default function ProfilePage({
  currentUser,
  setCurrentUser,
  handleFileChange,
  handleSaveCroppedAvatar,
  handleChangePassword,
  handleDeleteAccount,
  deleteAccountFormRef,
  passwordFormRef,
  showPasswordForm,
  setShowPasswordForm,
  currentPasswordInput,
  setCurrentPasswordInput,
  newPasswordInput,
  setNewPasswordInput,
  showDeleteAccountForm,
  setShowDeleteAccountForm,
  deleteAccountPassword,
  setDeleteAccountPassword,
  firstName,
  setFirstName,
  middleInitial,
  setMiddleInitial,
  lastName,
  setLastName,
  rank,
  setRank,
  assignment,
  setAssignment,
  aboutMe,
  setAboutMe,
  newState,
  setNewState,
  newAgency,
  setNewAgency,
  showCropModal,
  setShowCropModal,
  selectedImage,
  setSelectedImage,
  crop,
  setCrop,
  zoom,
  setZoom,
  croppedAreaPixels,
  setCroppedAreaPixels,
  onCropComplete,
  showEditModal,
  setShowEditModal,
  handleSaveProfileChanges,
  openProfileEditModal,
  isProfileEditLocked = false,
  profileEditLockMessage = '',
  confirmNameChange,
  renderEditModal = true,
}) {


  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingRankAssignment, setIsEditingRankAssignment] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [pendingNewEmail, setPendingNewEmail] = useState("");
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [emailVerifiedForChange, setEmailVerifiedForChange] = useState(false);


  // Certifications and Recommendations
  const [certifications, setCertifications] = useState([]);
  const [newCert, setNewCert] = useState('');
  const [recommendations, setRecommendations] = useState([]);
  const [recTitle, setRecTitle] = useState('');
  const [recDesc, setRecDesc] = useState('');

  // ✅ Years of Service (Sworn-in tracking)
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [firstSwornDate, setFirstSwornDate] = useState(currentUser.firstSwornIn || '');
  const [breaks, setBreaks] = useState(currentUser.breaksInService || []);
  const [totalYears, setTotalYears] = useState(currentUser.totalYearsOfService || null);

  // ✅ Law Enforcement Experience
  const [experienceList, setExperienceList] = useState([]);
  const [editingExp, setEditingExp] = useState(null);
  const [newExp, setNewExp] = useState({
    state: '',
    department: '',
    startDate: '',
    endDate: '',
    startRank: '',
    endRank: '',
    assignments: ''
  });
  const [showExpForm, setShowExpForm] = useState(false);


  const firstInputRef = useRef(null);
  const ABOUT_ME_WORD_LIMIT = 350;

  const getWordCount = (text) => {
    const trimmed = (text || '').trim();
    return trimmed ? trimmed.split(/\s+/).length : 0;
  };

  const limitTextByWords = (text, limit) => {
    if (!text) return '';
    let count = 0;
    const regex = /\S+/g;
    let match;
    while ((match = regex.exec(text))) {
      count += 1;
      if (count > limit) {
        return text.slice(0, match.index).trimEnd();
      }
    }
    return text;
  };

  const handleAboutMeChange = (event) => {
    const nextValue = event.target.value;
    const nextCount = getWordCount(nextValue);
    const currentCount = getWordCount(aboutMe);

    if (nextCount > ABOUT_ME_WORD_LIMIT) {
      const limited = limitTextByWords(nextValue, ABOUT_ME_WORD_LIMIT);
      setAboutMe(limited);
      return;
    }

    if (currentCount >= ABOUT_ME_WORD_LIMIT && nextValue.length > aboutMe.length) {
      return;
    }

    const limited = limitTextByWords(nextValue, ABOUT_ME_WORD_LIMIT);
    setAboutMe(limited);
  };

  const aboutMeWordCount = getWordCount(aboutMe);
  const isAboutMeAtLimit = aboutMeWordCount >= ABOUT_ME_WORD_LIMIT;


  // Prevent body scroll while dragging on mobile
  useEffect(() => {
    const preventTouchMove = (e) => {
      if (document.body.classList.contains('dragging')) e.preventDefault();
    };
    document.addEventListener('touchmove', preventTouchMove, { passive: false });
    return () => document.removeEventListener('touchmove', preventTouchMove);
  }, []);



  useEffect(() => {
    if (editingExp && showExpForm) {
      setTimeout(() => firstInputRef.current?.focus(), 100);
    }
  }, [editingExp, showExpForm]);




  // 🔄 Load experience entries
  useEffect(() => {
    const fetchExperience = async () => {
      try {
        const expRef = collection(db, 'users', currentUser.uid, 'experience');
        const expSnap = await getDocs(expRef);
        const loaded = expSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        loaded.sort((a, b) => {
          const parseDate = (str) => {
            if (!str) return new Date(0);
            if (str.toLowerCase().includes('present')) return new Date();
            const [mm, yyyy] = str.split('/');
            return new Date(parseInt(yyyy) || 0, (parseInt(mm) || 1) - 1, 1);
          };
          return parseDate(b.endDate) - parseDate(a.endDate);
        });
        const ids = loaded.map(x => x.id);
        const duplicates = ids.filter((id, i) => ids.indexOf(id) !== i);
        if (duplicates.length) console.warn("⚠️ Duplicate experience IDs found:", duplicates);

        // ✅ Deduplicate
        const unique = Array.from(new Map(loaded.map(item => [item.id, item])).values());

        // ✅ Sort by position if defined, otherwise fallback to newest endDate
        unique.sort((a, b) => {
          const hasPosA = typeof a.position === 'number';
          const hasPosB = typeof b.position === 'number';

          if (hasPosA && hasPosB) return a.position - b.position;
          if (hasPosA && !hasPosB) return -1;
          if (!hasPosA && hasPosB) return 1;

          // fallback: sort newest endDate first
          const parseDate = (str) => {
            if (!str) return new Date(0);
            if (typeof str === 'string' && str.toLowerCase().includes('present')) return new Date();
            const [mm, yyyy] = str.split('/');
            return new Date(parseInt(yyyy) || 0, (parseInt(mm) || 1) - 1, 1);
          };
          return parseDate(b.endDate) - parseDate(a.endDate);
        });

        setExperienceList(unique);

      } catch (err) {
        console.error('Error loading experience:', err);
      }
    };
    if (currentUser?.uid) fetchExperience();
  }, [currentUser]);



  // ➕ Add or update experience
  const addExperience = async () => {
    const { state, department, startDate, endDate, startRank, endRank, assignments } = newExp;
    if (!department || !startDate || !endDate) return alert('Please fill required fields.');

    try {
      // helper to parse mm/yyyy or "Present"
      const parseDate = (str) => {
        if (!str) return new Date(0);
        if (typeof str === "string" && str.toLowerCase().includes("present")) return new Date();
        const [mm, yyyy] = str.split("/");
        return new Date(parseInt(yyyy) || 0, (parseInt(mm) || 1) - 1, 1);
      };

      // update existing experience
      if (editingExp) {
        const expRef = doc(db, 'users', currentUser.uid, 'experience', editingExp.id);
        await updateDoc(expRef, { ...newExp, updatedAt: new Date() });

        setExperienceList(prev => {
          const updated = prev.map(e => (e.id === editingExp.id ? { ...e, ...newExp } : e));
          return updated.sort((a, b) => parseDate(b.endDate) - parseDate(a.endDate));
        });

        setEditingExp(null);
      }
      // add new experience
      else {
        const expRef = collection(db, 'users', currentUser.uid, 'experience');
        const position = experienceList.length; // last position
        const docRef = await addDoc(expRef, { ...newExp, createdAt: new Date(), position });

        setExperienceList(prev => {
          const updated = [...prev, { id: docRef.id, ...newExp }];
          return updated.sort((a, b) => parseDate(b.endDate) - parseDate(a.endDate));
        });
      }

      // reset form
      setNewExp({
        state: '',
        department: '',
        startDate: '',
        endDate: '',
        startRank: '',
        endRank: '',
        assignments: ''
      });
      setShowExpForm(false);
    } catch (err) {
      console.error('Error saving experience:', err);
    }
  };


  // 🧮 Calculate total years excluding breaks
  // 🧮 Calculate and auto-update total years excluding breaks
  useEffect(() => {
    if (!firstSwornDate) return setTotalYears(null);

    const [mm, yyyy] = firstSwornDate.split('/');
    if (!mm || !yyyy) return setTotalYears(null);

    const start = new Date(parseInt(yyyy), parseInt(mm) - 1, 1);
    const now = new Date();

    let diff = now - start;

    // Subtract break durations
    breaks.forEach((b) => {
      if (b.from && b.till) {
        const [bfm, bfy] = b.from.split('/');
        const [btm, bty] = b.till.split('/');
        if (bfm && bfy && btm && bty) {
          const from = new Date(parseInt(bfy), parseInt(bfm) - 1, 1);
          const till = new Date(parseInt(bty), parseInt(btm) - 1, 1);
          diff -= till - from;
        }
      }
    });

    const years = diff / (1000 * 60 * 60 * 24 * 365.25);
    const formattedYears = parseFloat(years.toFixed(1));
    setTotalYears(formattedYears);

    // ✅ Auto-sync yearly: update Firestore if value drifts ≥0.1
    const lastStored = parseFloat(currentUser.totalYearsOfService || 0);
    if (Math.abs(formattedYears - lastStored) >= 0.1) {
      updateDoc(doc(db, 'users', currentUser.uid), {
        totalYearsOfService: formattedYears,
        updatedAt: new Date(),
      })
        .then(() => {
          console.log('🔄 Auto-updated years of service:', formattedYears);
          setCurrentUser((prev) => ({
            ...prev,
            totalYearsOfService: formattedYears,
          }));
        })
        .catch((err) =>
          console.error('⚠️ Failed to auto-update years of service:', err)
        );
    }
  }, [firstSwornDate, breaks, currentUser.uid]);



  // ➕ Add or remove breaks
  const addBreak = () => setBreaks([...breaks, { from: '', till: '' }]);
  const removeBreak = (index) => setBreaks(breaks.filter((_, i) => i !== index));

  // 💾 Save service info to Firestore
  const saveServiceInfo = async () => {
    try {
      const dataToSave = {
        firstSwornIn: firstSwornDate,
        breaksInService: breaks && breaks.length ? breaks : [],
        totalYearsOfService: totalYears,
        updatedAt: new Date(),
      };

      await updateDoc(doc(db, 'users', currentUser.uid), dataToSave);

      // ✅ also update local user object so UI stays consistent
      setCurrentUser((prev) => ({
        ...prev,
        ...dataToSave,
      }));

      setShowServiceModal(false);
      alert('Years of service updated.');
    } catch (err) {
      console.error('Error saving service info:', err);
    }
  };



  // ❌ Delete experience
  const removeExperience = async (id) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this experience entry?');
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'experience', id));
      setExperienceList((prev) => prev.filter((e) => e.id !== id));
    } catch (err) {
      console.error('Error deleting experience:', err);
    }
  };



  // 🔄 Load data from Firestore when page mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Load certifications
        const certRef = collection(db, "users", currentUser.uid, "certifications");
        const certSnap = await getDocs(certRef);
        setCertifications(certSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        // Load recommendations
        const recRef = collection(db, "users", currentUser.uid, "recommendations");
        const recSnap = await getDocs(recRef);
        setRecommendations(recSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error("Error loading certifications/recommendations:", error);
      }
    };

    if (currentUser?.uid) fetchData();
  }, [currentUser]);



  // Avatar upload handlers
  const handleAvatarClick = () => {
    document.getElementById('avatar-file-input').click();
  };



  // ✅ Add / Remove certifications (Firestore)
  const addCertification = async () => {
    if (!newCert.trim()) return;
    try {
      const certRef = collection(db, "users", currentUser.uid, "certifications");
      const docRef = await addDoc(certRef, { name: newCert.trim(), createdAt: new Date() });
      setCertifications([...certifications, { id: docRef.id, name: newCert.trim() }]);
      setNewCert('');
    } catch (err) {
      console.error("Error adding certification:", err);
    }
  };

  const removeCertification = async (certId) => {
    try {
      await deleteDoc(doc(db, "users", currentUser.uid, "certifications", certId));
      setCertifications(certifications.filter((c) => c.id !== certId));
    } catch (err) {
      console.error("Error removing certification:", err);
    }
  };

  // ✅ Add recommendation (Firestore)
  const addRecommendation = async () => {
    if (!recTitle.trim() || !recDesc.trim()) return;
    try {
      const recRef = collection(db, "users", currentUser.uid, "recommendations");
      const docRef = await addDoc(recRef, {
        title: recTitle.trim(),
        description: recDesc.trim(),
        createdAt: new Date(),
      });
      setRecommendations([...recommendations, { id: docRef.id, title: recTitle.trim(), description: recDesc.trim() }]);
      setRecTitle('');
      setRecDesc('');
    } catch (err) {
      console.error("Error adding recommendation:", err);
    }
  };


  const removeRecommendation = async (recId) => {
    const confirmDelete = window.confirm("Delete this recommendation?");
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, "users", currentUser.uid, "recommendations", recId));
      setRecommendations((prev) => prev.filter((r) => r.id !== recId));
    } catch (err) {
      console.error("Error removing recommendation:", err);
    }
  };




  // 🧱 Draggable experience card (fixed)
  function SortableItem({ exp, onEdit, onRemove }) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: exp.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    const { department, state, startDate, endDate, assignments, startRank, endRank } = exp;
    const sameRank = startRank === endRank;

    return (
      <div ref={setNodeRef} style={style} className="experience-card">
        {/* Drag handle */}
        <div className="drag-handle" {...attributes} {...listeners}>
          <div className="handle-arrows">
            <span className="arrow-up">▲</span>
            <span className="dots">⋮</span>
            <span className="arrow-down">▼</span>
          </div>
        </div>

        {/* Edit / Delete buttons */}
        <span
          className="edit-exp"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
        >
          ✏️
        </span>
        <span
          className="remove-exp"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          ×
        </span>

        {/* Info block */}
        <div className="exp-card-info">
          <div><strong>{department}</strong> ({state})</div>
          <div>{startDate} – {endDate || 'Present'}</div>
          <div>
            {assignments && <span>{assignments}, </span>}
            {sameRank ? endRank : `${startRank} → ${endRank}`}
          </div>
        </div>
      </div>
    );
  }



  // 🧠 Enable both mouse and touch dragging (with delay for touch)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // must move at least 8px before starting drag (desktop)
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 180,     // short hold before drag starts (ms)
        tolerance: 8,   // small finger movement allowed
      },
      pressDelay: 180,  // safety for some browsers
    })
  );





  return (
    <div className="profile-page-container">
      {/* Banner and Avatar */}
      <div className="profile-banner">
        <div className="agency-banner">
          {`${currentUser.agency || 'Agency'} // `.repeat(8)}
        </div>
        <div className="avatar-container top-center">
          <img
            src={currentUser.avatarUrl || '/avatar-image.png'}
            alt="Profile"
            className="avatar-image"
            onClick={handleAvatarClick}
          />
          <input
            type="file"
            accept="image/*"
            id="avatar-file-input"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
      </div>

      <div className="profile-account-heading">
        <h2 className="text-center">Account Information</h2>
        {currentUser?.isFederal === true && (
          <div className="profile-fed-badge-row">
            <span className="profile-fed-badge" title="Federal employee">F</span>
          </div>
        )}
      </div>

      {/* CURRENT EMPLOYMENT INFO */}
      <div className="employment-info">
        <div className="field"><label>State:</label> <input type="text" value={currentUser.state} readOnly /></div>
        <div className="field"><label>First Name:</label> <input type="text" value={firstName} readOnly /></div>
        <div className="field"><label>Middle Initial:</label> <input type="text" value={middleInitial} readOnly /></div>
        <div className="field"><label>Last Name:</label> <input type="text" value={lastName} readOnly /></div>
        <div className="field"><label>Rank:</label> <input type="text" value={rank} readOnly /></div>
        <div className="field"><label>Assignment:</label> <input type="text" value={assignment} readOnly /></div>
        <div className="field"><label>Agency:</label> <input type="text" value={currentUser.agency} readOnly /></div>
        <div className="field"><label>Email:</label> <input type="email" value={currentUser.email} readOnly /></div>
        <div className="field"><label>User Number:</label> <input type="text" value={currentUser.userNumber} readOnly /></div>
      </div>

      {/* EDIT PROFILE BUTTON */}
      <div className="edit-button-container">
        <button
          onClick={() => (openProfileEditModal ? openProfileEditModal() : setShowEditModal(true))}
          className="edit-profile-button"
          disabled={isProfileEditLocked}
          style={{
            opacity: isProfileEditLocked ? 0.6 : 1,
            cursor: isProfileEditLocked ? 'not-allowed' : 'pointer',
          }}
        >
          Edit Profile
        </button>
        {profileEditLockMessage && (
          <p style={{ color: '#8a6d3b', fontSize: '0.9em', marginTop: '8px' }}>
            {profileEditLockMessage}
          </p>
        )}
      </div>

      <div className="about-me-section">
        <h3 className="section-title">Introduce yourself:</h3>
        <textarea
          className="about-me-textarea"
          placeholder="Share a brief introduction with other users"
          value={aboutMe}
          onChange={handleAboutMeChange}
          rows={5}
        />
        <div className={`about-me-meta${isAboutMeAtLimit ? ' limit-reached' : ''}`}>
          {aboutMeWordCount}/{ABOUT_ME_WORD_LIMIT} words
        </div>
        <div className="about-me-actions">
          <button className="edit-profile-button" onClick={handleSaveProfileChanges}>
            Save Introduction
          </button>
        </div>
      </div>

      {/* MAIN TWO-COLUMN LAYOUT */}
      <div className="profile-main-sections">
        {/* LEFT COLUMN - EXPERIENCE */}
        <div className="left-column">
          <h3 className="section-title">Law Enforcement Experience</h3>
          {/* YEARS OF SERVICE */}
          <div className="years-of-service-header">
            <span>
              Years of Service:{' '}
              {(totalYears || currentUser.totalYearsOfService) ? (
                <strong>{totalYears || currentUser.totalYearsOfService}</strong>
              ) : (
                <span className="add-service-link" onClick={() => setShowServiceModal(true)}>
                  Add
                </span>
              )}
            </span>
            {totalYears && (
              <button className="edit-service-btn" onClick={() => setShowServiceModal(true)}>
                Edit
              </button>
            )}
          </div>

          {/* Modal for Date First Sworn + Breaks */}
          {showServiceModal && (
            <div className="modal-overlay" onClick={(e) => e.target.classList.contains('modal-overlay') && setShowServiceModal(false)}>
              <div className="modal-content small">
                <h3>Years of Service</h3>

                {/* First Sworn Date */}
                <label>Date First Sworn as Law Enforcement Officer (mm/yyyy):</label>
                <input
                  type="text"
                  placeholder="mm/yyyy"
                  value={firstSwornDate}
                  onChange={(e) => {
                    let v = e.target.value.replace(/\D/g, '');
                    if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2, 6);
                    setFirstSwornDate(v);
                  }}
                  maxLength={7}
                />

                {/* Breaks in Service */}
                <h4>Breaks in Service (if any)</h4>
                {breaks.map((b, i) => (
                  <div key={i} className="break-row">
                    <input
                      type="text"
                      placeholder="From (mm/yyyy)"
                      value={b.from}
                      onChange={(e) => {
                        let v = e.target.value.replace(/\D/g, '');
                        if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2, 6);
                        const updated = [...breaks];
                        updated[i].from = v;
                        setBreaks(updated);
                      }}
                      maxLength={7}
                    />
                    <input
                      type="text"
                      placeholder="To (mm/yyyy)"
                      value={b.till}
                      onChange={(e) => {
                        let v = e.target.value.replace(/\D/g, '');
                        if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2, 6);
                        const updated = [...breaks];
                        updated[i].till = v;
                        setBreaks(updated);
                      }}
                      maxLength={7}
                    />
                    <span className="remove-break" onClick={() => removeBreak(i)}>×</span>
                  </div>
                ))}

                <button className="add-break-btn" onClick={addBreak}>+ Add Break</button>

                <div className="modal-buttons">
                  <button onClick={saveServiceInfo}>Save</button>
                  <button onClick={() => setShowServiceModal(false)}>Cancel</button>
                </div>
              </div>
            </div>
          )}


          {/* ➕ Add Experience Button */}
          <button className="add-exp-btn" onClick={() => setShowExpForm(!showExpForm)}>
            {showExpForm ? 'Cancel' : '+ Add Experience'}
          </button>

          {/* New Experience Form */}
          {showExpForm && (
            <div className="experience-form">
              <p className="form-instruction">
                Start with your <strong>first</strong> department.
              </p>
              <select
                value={newExp.state || currentUser.state}
                onChange={e => setNewExp({ ...newExp, state: e.target.value })}
              >
                <option value="">Select State</option>
                {[
                  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
                  'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA',
                  'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
                ].map((abbr) => (
                  <option key={abbr} value={abbr}>{abbr}</option>
                ))}
              </select>

              <input
                ref={firstInputRef}
                placeholder="Department"
                value={newExp.department}
                onChange={(e) => setNewExp({ ...newExp, department: e.target.value })}
              />
              <div className="two-cols">
                <input
                  placeholder="Start (mm/yyyy)"
                  value={newExp.startDate}
                  onChange={(e) => {
                    let v = e.target.value.replace(/\D/g, '');
                    if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2, 6);
                    setNewExp({ ...newExp, startDate: v });
                  }}
                  maxLength={7}
                />
                <input
                  placeholder="End (mm/yyyy or Present)"
                  value={newExp.endDate}
                  onChange={(e) => {
                    let v = e.target.value.replace(/\D/g, '');
                    if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2, 6);
                    setNewExp({ ...newExp, endDate: v });
                  }}
                  maxLength={7}
                />

              </div>
              <div className="two-cols">
                <input placeholder="Starting Rank" value={newExp.startRank} onChange={e => setNewExp({ ...newExp, startRank: e.target.value })} />
                <input placeholder="End Rank" value={newExp.endRank} onChange={e => setNewExp({ ...newExp, endRank: e.target.value })} />
              </div>
              <textarea placeholder="Assignments (e.g., Patrol, Investigations...)" value={newExp.assignments} onChange={e => setNewExp({ ...newExp, assignments: e.target.value })}></textarea>
              <button onClick={addExperience} className="btn-success">
                {editingExp ? 'Update Experience' : 'Save Experience'}
              </button>
            </div>
          )}


          {/* Show Experience List (Draggable – Step 1 Visual Only) */}
          <div className="experience-section">
            {experienceList.length === 0 ? (
              <p className="empty-text">No prior experience added yet.</p>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={() => document.body.classList.add('dragging')}
                onDragEnd={async (event) => {
                  document.body.classList.remove('dragging'); // ✅ re-enable scroll
                  const { active, over } = event;
                  if (!over || active.id === over.id) return;

                  const oldIndex = experienceList.findIndex(e => e.id === active.id);
                  const newIndex = experienceList.findIndex(e => e.id === over.id);
                  const newOrder = arrayMove(experienceList, oldIndex, newIndex);
                  setExperienceList(newOrder);

                  try {
                    for (let i = 0; i < newOrder.length; i++) {
                      const expRef = doc(db, 'users', currentUser.uid, 'experience', newOrder[i].id);
                      await updateDoc(expRef, { position: i });
                    }
                  } catch (err) {
                    console.error('Error updating order:', err);
                  }
                }}
              >

                <SortableContext
                  items={experienceList.map(e => e.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {experienceList.map((exp) => (
                    <SortableItem
                      key={exp.id}
                      exp={exp}
                      onEdit={() => {
                        setNewExp(exp);
                        setEditingExp(exp);
                        setShowExpForm(true);
                      }}
                      onRemove={() => removeExperience(exp.id)}
                    />
                  ))}
                </SortableContext>
              </DndContext>

            )}
          </div>
        </div> {/* end of left-column */}


        {/* RIGHT COLUMN - CERTIFICATIONS & RECOMMENDATIONS */}
        <div className="right-column">
          <h3 className="section-title">Certifications</h3>
          <p className="certification-hint">
            List active certifications you currently hold.
          </p>

          {/* Add Certification Input */}
          <div className="add-cert">

            <input
              type="text"
              placeholder="Add certification..."
              value={newCert}
              onChange={(e) => setNewCert(e.target.value)}
            />
            <button onClick={addCertification}>Add</button>
          </div>

          {/* Display Certifications */}
          <div className="certifications-list">
            {certifications.length === 0 && <p className="empty-text">No certifications added yet.</p>}
            {certifications.map((cert) => (
              <div key={cert.id} className="cert-badge">
                {cert.name}
                <span className="remove-cert" onClick={() => removeCertification(cert.id)}>×</span>
              </div>
            ))}
          </div>

          {/* Recommendations */}
          <h3 className="section-title">I Recommend</h3>
          <p className="recommendation-hint">
            List any classes, certifications, or programs that have helped you in your career and that you would recommend to others.
          </p>
          <div className="recommendation-input">

            <input
              type="text"
              placeholder="Title (Course, Program, etc.)"
              value={recTitle}
              onChange={(e) => setRecTitle(e.target.value)}
            />
            <textarea
              placeholder="Describe why you recommend it..."
              value={recDesc}
              onChange={(e) => setRecDesc(e.target.value)}
            ></textarea>
            <button onClick={addRecommendation}>Add Recommendation</button>
          </div>

          {/* Display Recommendations */}
          <div className="recommendations-list">
            {recommendations.length === 0 && <p className="empty-text">No recommendations yet.</p>}
            {recommendations.map((rec) => (
              <div key={rec.id} className="recommendation-card">
                <strong>{rec.title}</strong>
                <p>{rec.description}</p>
                <span
                  className="remove-rec"
                  onClick={() => removeRecommendation(rec.id)}
                  title="Delete recommendation"
                >
                  ×
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* PASSWORD + DELETE BUTTONS */}
      <div className="bottom-actions">
        <button
          onClick={() => {
            setShowDeleteAccountForm(false);
            setDeleteAccountPassword('');
            setShowPasswordForm(true);
            setTimeout(() => {
              passwordFormRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 0);
          }}
          className="btn-primary"
        >
          Change Password
        </button>

        <button
          onClick={() => {
            setShowPasswordForm(false);
            setCurrentPasswordInput('');
            setNewPasswordInput('');
            setShowDeleteAccountForm(true);
            setTimeout(() => {
              deleteAccountFormRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 0);
          }}
          className="btn-danger"
        >
          Delete Account
        </button>
      </div>

      {/* PASSWORD FORM */}
      {showPasswordForm && (
        <div ref={passwordFormRef} className="password-form">
          <div className="form-group">
            <label>Current Password:</label>
            <input
              type="password"
              value={currentPasswordInput}
              onChange={(e) => setCurrentPasswordInput(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>New Password:</label>
            <input
              type="password"
              value={newPasswordInput}
              onChange={(e) => setNewPasswordInput(e.target.value)}
            />
          </div>
          <div className="button-group">
            <button onClick={handleChangePassword} className="btn-success">Submit</button>
            <button
              onClick={() => {
                setShowPasswordForm(false);
                setCurrentPasswordInput('');
                setNewPasswordInput('');
              }}
              className="btn-danger"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showDeleteAccountForm && (
        <div ref={deleteAccountFormRef} className="password-form">
          <div className="form-group">
            <label>Current Password:</label>
            <input
              type="password"
              value={deleteAccountPassword}
              onChange={(e) => setDeleteAccountPassword(e.target.value)}
            />
          </div>
          <div className="button-group">
            <button onClick={handleDeleteAccount} className="btn-danger">Delete Account</button>
            <button
              onClick={() => {
                setShowDeleteAccountForm(false);
                setDeleteAccountPassword('');
              }}
              className="btn-primary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {renderEditModal && showEditModal && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target.classList.contains('modal-overlay')) setShowEditModal(false);
          }}
        >
          <div className="modal-content">
            <h3>Edit Profile</h3>
            <div className="field">
              <label>State:</label>
              <input type="text" value={newState} onChange={(e) => setNewState(e.target.value)} />
            </div>
            <div className="field">
              <label>Last Name:</label>
              <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
            <div className="field">
              <label>Rank:</label>
              <input type="text" value={rank} onChange={(e) => setRank(e.target.value)} />
            </div>
            <div className="field">
              <label>Assignment:</label>
              <input type="text" value={assignment} onChange={(e) => setAssignment(e.target.value)} />
            </div>
            <div className="field">
              <label>Agency:</label>
              <input type="text" value={newAgency} onChange={(e) => setNewAgency(e.target.value)} />
            </div>
            <div className="modal-buttons">
              <button onClick={() => setShowEditModal(false)}>Cancel</button>
              <button onClick={handleSaveProfileChanges}>Save</button>
            </div>
            <p style={{ marginTop: '10px', fontSize: '13px', color: '#666', textAlign: 'center' }}>
              Personal info edits are limited to once per month.
            </p>
          </div>
        </div>
      )}

      {/* AVATAR CROP MODAL */}
      {showCropModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Edit Avatar</h3>
            <div className="crop-container">
              <Cropper
                image={selectedImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="controls">
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(e.target.value)}
              />
            </div>
            <div className="modal-buttons">
              <button onClick={() => setShowCropModal(false)}>Cancel</button>
              <button onClick={handleSaveCroppedAvatar}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 
